<script setup lang="ts">
/**
 * The bid workspace — ADR 0060 §5 applied to bid writing.
 *
 * A bid is a `#UID-` folder on the Bid Office SharePoint with a
 * requirement matrix at its spine. That is a workspace, not a
 * conversation: the team lives in the kravmatrix for weeks, assigning
 * owners and tracking what is answered, and every other artefact refers
 * to its rows. So the page IS the data — shared by everyone on the bid —
 * and the chat sits in the corner as a helper that receives the on-screen
 * state as `page_context` and whose row-writing tool calls make the page
 * reload.
 *
 * The list is read from the Bid Office folder tree (the backend rebuilds
 * it from the index), so nobody types bids in. What is in progress comes
 * first; what is delivered and what is archived stay a click away.
 */
import { computed, onMounted, onUnmounted, reactive, ref, watch } from "vue";

import AssistantPage, { type PageTab } from "../components/AssistantPage.vue";
import HelperPanel from "../components/HelperPanel.vue";
import { useHelperChat } from "../composables/useHelperChat";

import {
  type Bid,
  type BidDetail,
  type BidRequirement,
  type Decision,
  type GeneratedDoc,
  createBid,
  downloadAuthed,
  getBid,
  listBids,
  listRequirements,
  patchRequirement,
  RequirementConflict,
  setDecision,
  uploadToBid,
} from "../api/bids";

const props = defineProps<{ devUser: string; bidUid: string | null }>();
const emit = defineEmits<{ (e: "exit"): void }>();

// ── shared state ───────────────────────────────────────────────────────
const loading = ref(false);
const error = ref("");
/** A transient line at the top of the workspace ("Bo ændrede MK-3 imens"). */
const notice = ref("");
let noticeTimer: ReturnType<typeof setTimeout> | null = null;
function say(text: string): void {
  notice.value = text;
  if (noticeTimer) clearTimeout(noticeTimer);
  noticeTimer = setTimeout(() => (notice.value = ""), 8000);
}

const STATUS_LABEL: Record<string, string> = {
  open: "Åben",
  draft: "Udkast",
  answered: "Besvaret",
  verified: "Verificeret",
  na: "Ikke relevant",
};
const A_FOLDER = "A] Udbudsmateriale (Må ikke ændres)";
const DAY = 86_400_000;

function daysUntil(iso: string): number | null {
  if (!iso) return null;
  const d = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(d.getTime())) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Math.round((d.getTime() - today.getTime()) / DAY);
}

/** "i dag", "om 3 dage", "for 2 dage siden" — or the words as written. */
function deadlineLabel(b: Pick<Bid, "deadline" | "deadline_text" | "deadline_time">): string {
  const n = daysUntil(b.deadline);
  if (n === null) return b.deadline_text || "Ingen tidsfrist";
  const t = b.deadline_time ? ` kl. ${b.deadline_time}` : "";
  if (n === 0) return `I dag${t}`;
  if (n === 1) return `I morgen${t}`;
  if (n === -1) return "I går";
  if (n > 0) return `Om ${n} dage${n <= 7 ? t : ""}`;
  return `For ${-n} dage siden`;
}
function deadlineTone(b: Pick<Bid, "deadline">): "" | "warn" | "hot" {
  const n = daysUntil(b.deadline);
  if (n === null || n < 0) return "";
  if (n <= 3) return "hot";
  if (n <= 7) return "warn";
  return "";
}
function fmtDate(iso: string): string {
  if (!iso) return "";
  const [y, m, d] = iso.split("-");
  return `${d}.${m}.${y}`;
}
function relTime(iso: string): string {
  if (!iso) return "";
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return "";
  const mins = Math.round((Date.now() - t) / 60_000);
  if (mins < 1) return "lige nu";
  if (mins < 60) return `for ${mins} min siden`;
  const h = Math.round(mins / 60);
  if (h < 24) return `for ${h} t siden`;
  const d = Math.round(h / 24);
  return d === 1 ? "i går" : `for ${d} dage siden`;
}

// ── list view ──────────────────────────────────────────────────────────
const bids = ref<Bid[]>([]);
const archivedCount = ref(0);
const rebuiltAt = ref("");
const showArchived = ref(false);
const query = ref("");
const newOpen = ref(false);
const creating = ref(false);
const form = reactive({ uid: "", customer: "", title: "", framework: "", deadline: "" });

async function loadList(): Promise<void> {
  loading.value = true;
  error.value = "";
  try {
    const out = await listBids(props.devUser, showArchived.value);
    bids.value = out.bids;
    archivedCount.value = out.archived_count;
    rebuiltAt.value = out.rebuilt_at;
  } catch (e) {
    error.value = e instanceof Error ? e.message : String(e);
  } finally {
    loading.value = false;
  }
}

function matches(b: Bid, q: string): boolean {
  if (!q) return true;
  const hay = `${b.uid} ${b.customer} ${b.title} ${b.framework} ${b.folder_name}`.toLowerCase();
  return q
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean)
    .every((w) => hay.includes(w));
}
const filtered = computed(() => bids.value.filter((b) => matches(b, query.value.trim())));
const byDeadlineAsc = (a: Bid, b: Bid) =>
  (a.deadline || "9999").localeCompare(b.deadline || "9999") || b.uid.localeCompare(a.uid);
const byDeadlineDesc = (a: Bid, b: Bid) => (b.deadline || "").localeCompare(a.deadline || "");

const soon = computed(() =>
  filtered.value
    .filter((b) => b.phase === "aktiv" && (daysUntil(b.deadline) ?? 99) <= 7)
    .sort(byDeadlineAsc),
);
/** Not yet decided: the list's first question is "skal vi byde?". */
const undecided = computed(() =>
  filtered.value
    .filter((b) => b.phase === "aktiv" && (daysUntil(b.deadline) ?? 99) > 7 && b.decision !== "bid")
    .sort(byDeadlineAsc),
);
const active = computed(() =>
  filtered.value
    .filter((b) => b.phase === "aktiv" && (daysUntil(b.deadline) ?? 99) > 7 && b.decision === "bid")
    .sort(byDeadlineAsc),
);
const delivered = computed(() =>
  filtered.value.filter((b) => b.phase === "afleveret").sort(byDeadlineDesc),
);
const declined = computed(() =>
  filtered.value.filter((b) => b.phase === "fravalgt").sort(byDeadlineDesc),
);
const declinedOpen = ref(false);
const archived = computed(() =>
  filtered.value.filter((b) => b.phase === "arkiv").sort(byDeadlineDesc),
);
const deliveredOpen = ref(false);

async function createNew(): Promise<void> {
  creating.value = true;
  error.value = "";
  try {
    const b = await createBid(props.devUser, {
      uid: form.uid.trim(),
      customer: form.customer.trim(),
      title: form.title.trim(),
      framework: form.framework.trim(),
      deadline: form.deadline,
    });
    window.location.hash = `#/bid/${b.uid}`;
  } catch (e) {
    error.value = e instanceof Error ? e.message : String(e);
  } finally {
    creating.value = false;
  }
}
function openBid(uid: string): void {
  window.location.hash = `#/bid/${uid}`;
}
function gotoList(): void {
  window.location.hash = "#/bid";
}

// ── workspace ──────────────────────────────────────────────────────────
const detail = ref<BidDetail | null>(null);
const rows = ref<BidRequirement[]>([]);
const statuses = ref<string[]>(["open", "draft", "answered", "verified", "na"]);
type Tab = "beslutning" | "krav" | "aflevering" | "strategi" | "dokumenter" | "qa";
const tab = ref<Tab>("krav");
/** Which tab a stage in the strip opens. */
const STAGE_TAB: Record<string, Tab> = {
  beslutning: "beslutning",
  materiale: "dokumenter",
  analyse: "krav",
  aflevering: "aflevering",
  strategi: "strategi",
  skriv: "dokumenter",
  qa: "qa",
};
function goStage(key: string): void {
  tab.value = STAGE_TAB[key] ?? "krav";
}
function isTab(v: string): v is Tab {
  return ["beslutning", "krav", "aflevering", "strategi", "dokumenter", "qa"].includes(v);
}
/** The next-step button: start the request in the helper, or just go there. */
function runNext(): void {
  const n = detail.value?.next_step;
  if (!n) return;
  if (isTab(n.tab)) tab.value = n.tab;
  if (n.ask) askHelper(n.ask);
}
const decisionNote = ref("");
const deciding = ref(false);
async function decide(d: Decision): Promise<void> {
  const uid = props.bidUid;
  if (!uid || !detail.value) return;
  deciding.value = true;
  error.value = "";
  try {
    detail.value.bid = await setDecision(props.devUser, uid, d, decisionNote.value.trim());
    decisionNote.value = "";
    await refresh();
    if (d === "bid") tab.value = rows.value.length ? "krav" : "krav";
  } catch (e) {
    error.value = e instanceof Error ? e.message : String(e);
  } finally {
    deciding.value = false;
  }
}
const VERDICT_MARK: Record<string, string> = { ok: "✓", risk: "▲", stop: "✖", ukendt: "?" };
const REC_LABEL: Record<string, string> = { bid: "Bid", nobid: "No-bid", uklart: "Uklart" };
type Filter = "alle" | "must" | "udenejer" | "udensvar";
const filter = ref<Filter>("alle");
const selectedId = ref<number | null>(null);
const uploading = ref(false);
const zipping = ref(false);

const bid = computed(() => detail.value?.bid ?? null);
const conversationId = computed(() => detail.value?.conversation_id ?? null);

async function loadBid(): Promise<void> {
  const uid = props.bidUid;
  if (!uid) return;
  loading.value = true;
  error.value = "";
  try {
    const [d, r] = await Promise.all([getBid(props.devUser, uid), listRequirements(props.devUser, uid)]);
    detail.value = d;
    rows.value = r.rows;
    statuses.value = r.statuses;
    // Land where the work is: the decision until it is taken, the matrix after.
    tab.value = d.bid.decision === "bid" ? "krav" : "beslutning";
  } catch (e) {
    error.value = e instanceof Error ? e.message : String(e);
  } finally {
    loading.value = false;
  }
}

/** Reload what changes between turns and between colleagues. Cheap
 * enough to call after every helper turn, on focus, and on a timer. A
 * row someone else changed is noticed here, so the notice names them. */
async function refresh(): Promise<void> {
  const uid = props.bidUid;
  if (!uid) return;
  try {
    const [d, r] = await Promise.all([getBid(props.devUser, uid), listRequirements(props.devUser, uid)]);
    const before = new Map(rows.value.map((x) => [x.id, x]));
    const changedByOthers = r.rows.filter((x) => {
      const old = before.get(x.id);
      return old && old.version !== x.version && x.updated_by_name && !helperBusy.value;
    });
    detail.value = d;
    rows.value = r.rows;
    if (changedByOthers.length === 1) {
      const c = changedByOthers[0];
      say(`${c.updated_by_name} ændrede ${c.krav_id || "en række"} ${relTime(c.updated_at)}.`);
    } else if (changedByOthers.length > 1) {
      say(`${changedByOthers.length} rækker blev ændret af kolleger.`);
    }
  } catch (e) {
    error.value = e instanceof Error ? e.message : String(e);
  }
}

// keep the page honest while it is open: on focus, and every 30 s while visible
let pollTimer: ReturnType<typeof setInterval> | null = null;
function onFocus(): void {
  if (props.bidUid && document.visibilityState === "visible") void refresh();
}
onMounted(() => {
  window.addEventListener("focus", onFocus);
  document.addEventListener("visibilitychange", onFocus);
  pollTimer = setInterval(() => {
    if (props.bidUid && document.visibilityState === "visible" && !helperBusy.value) void refresh();
  }, 30_000);
});
onUnmounted(() => {
  window.removeEventListener("focus", onFocus);
  document.removeEventListener("visibilitychange", onFocus);
  if (pollTimer) clearInterval(pollTimer);
});

// ── requirements ───────────────────────────────────────────────────────
const shown = computed(() =>
  rows.value.filter((r) => {
    if (filter.value === "must") return r.must_should === "Must";
    if (filter.value === "udenejer") return !r.owner;
    if (filter.value === "udensvar") return !r.answered_in;
    return true;
  }),
);
const selected = computed(() => rows.value.find((r) => r.id === selectedId.value) ?? null);
const counts = computed(() => ({
  alle: rows.value.length,
  must: rows.value.filter((r) => r.must_should === "Must").length,
  udenejer: rows.value.filter((r) => !r.owner).length,
  udensvar: rows.value.filter((r) => !r.answered_in).length,
}));

/** Optimistic edit of one team field; the server is the truth on return.
 * A version conflict means a colleague wrote first: show their value,
 * say who, and leave the decision to the person. */
async function setField(
  row: BidRequirement,
  key: "owner" | "status" | "answered_in" | "note",
  value: string,
): Promise<void> {
  const uid = props.bidUid;
  if (!uid || row[key] === value) return;
  const before = row[key];
  row[key] = value;
  try {
    const saved = await patchRequirement(props.devUser, uid, row.id, { [key]: value }, row.version);
    Object.assign(row, saved);
    if (key === "owner" || key === "status") {
      detail.value = await getBid(props.devUser, uid);
    }
  } catch (e) {
    if (e instanceof RequirementConflict) {
      Object.assign(row, e.current);
      say(
        `${e.current.updated_by_name || "En kollega"} ændrede ${row.krav_id || "rækken"} imens — viser deres værdi. Ret igen hvis din skal gælde.`,
      );
      return;
    }
    row[key] = before;
    error.value = e instanceof Error ? e.message : String(e);
  }
}

// ── documents ──────────────────────────────────────────────────────────
function docsFor(t: Tab): GeneratedDoc[] {
  const needle =
    t === "aflevering" ? "afleveringsoversigt" : t === "strategi" ? "angrebsplan" : t === "qa" ? "qa" : "";
  if (!needle) return [];
  return (detail.value?.generated ?? []).filter((e) => e.title.toLowerCase().includes(needle));
}
async function downloadDoc(e: GeneratedDoc): Promise<void> {
  try {
    await downloadAuthed(props.devUser, e.download_url, `${e.title}.${e.format || "docx"}`);
  } catch (err) {
    error.value = err instanceof Error ? err.message : String(err);
  }
}
async function downloadZip(): Promise<void> {
  const uid = props.bidUid;
  if (!uid) return;
  zipping.value = true;
  try {
    await downloadAuthed(props.devUser, `/api/bids/${uid}/project.zip`, `UID-${uid}.zip`);
  } catch (e) {
    error.value = e instanceof Error ? e.message : String(e);
  } finally {
    zipping.value = false;
  }
}
async function exportXlsx(): Promise<void> {
  const uid = props.bidUid;
  if (!uid) return;
  try {
    await downloadAuthed(props.devUser, `/api/bids/${uid}/requirements.xlsx`, "Kravmatrix.xlsx");
  } catch (e) {
    error.value = e instanceof Error ? e.message : String(e);
  }
}
/** Files dropped here go into the caller's own conversation on the bid,
 * under A] — for material not in the SharePoint folder yet. */
async function onFiles(ev: Event): Promise<void> {
  const cid = conversationId.value;
  const input = ev.target as HTMLInputElement;
  const files = Array.from(input.files ?? []);
  if (cid === null || !files.length) return;
  uploading.value = true;
  error.value = "";
  try {
    for (const f of files) {
      const rel = f.name.toLowerCase().endsWith(".zip") ? f.name : `${A_FOLDER}/${f.name}`;
      await uploadToBid(props.devUser, cid, f, rel);
    }
    await refresh();
  } catch (e) {
    error.value = e instanceof Error ? e.message : String(e);
  } finally {
    uploading.value = false;
    input.value = "";
  }
}
const hasMaterial = computed(
  () => (detail.value?.counts.documents ?? 0) > 0 || (detail.value?.counts.uploads ?? 0) > 0,
);

// ── helper chat ────────────────────────────────────────────────────────
/** What the helper is told about the screen, each turn. Short on purpose:
 * it rides on every request. The bid itself (files, stage) is injected
 * server-side. */
function pageContext(): string {
  const parts: string[] = [];
  const d = detail.value;
  if (d) {
    parts.push(`Bid page open on tab '${tab.value}' for #UID-${d.bid.uid}.`);
    parts.push(
      `Kravmatrix: ${d.counts.total} rows, ${d.counts.must} Must, ${d.counts.must_unowned} Must without owner, ${d.counts.unanswered} without 'hvor besvares'.`,
    );
    parts.push(`Stages done: ${d.stages.filter((s) => s.done).map((s) => s.label).join(", ") || "none"}.`);
  }
  if (selected.value) {
    const r = selected.value;
    parts.push(
      `Selected requirement ${r.krav_id}: "${r.text.slice(0, 200)}" (${r.req_type} ${r.must_should}; owner ${r.owner || "none"}; status ${r.status}; answered in ${r.answered_in || "—"}).`,
    );
  }
  return parts.join(" ");
}
const helper = useHelperChat({
  assistantSlug: "bid-writing",
  devUser: () => props.devUser,
  conversationId: () => conversationId.value,
  pageContext,
  // Refresh after every turn, not only when a tool wrote: rows the helper
  // merged and documents it built both surface here.
  afterTurn: () => refresh(),
});
function askHelper(text: string): void {
  helper.ask(text);
}
const helperBusy = helper.busy;

const workspaceTabs = computed<PageTab[]>(() => [
  { key: "beslutning", label: "Beslutning", dot: !bid.value?.decision },
  { key: "krav", label: "Krav", count: rows.value.length || "" },
  { key: "aflevering", label: "Aflevering" },
  { key: "strategi", label: "Strategi" },
  {
    key: "dokumenter",
    label: "Dokumenter",
    count: detail.value
      ? detail.value.counts.documents + detail.value.counts.generated + detail.value.counts.uploads || ""
      : "",
  },
  { key: "qa", label: "QA" },
]);
const currentStage = computed(
  () => Object.entries(STAGE_TAB).find(([, t]) => t === tab.value)?.[0] ?? "",
);
function onTab(key: string): void {
  if (isTab(key)) tab.value = key;
}

const heading = computed(() => {
  const b = bid.value;
  if (!b) return "Bud";
  if (b.customer && b.title) return `${b.customer} — ${b.title}`;
  return b.customer || b.title || `#UID-${b.uid}`;
});

watch(
  () => props.bidUid,
  (uid) => {
    detail.value = null;
    rows.value = [];
    selectedId.value = null;
    notice.value = "";
    helper.reset();
    tab.value = "krav";
    if (!uid) void loadList();
    else void loadBid();
  },
  { immediate: true },
);
watch(showArchived, () => void loadList());
</script>

<template>
  <div class="bw">
    <!-- ── list view ─────────────────────────────────────────────────── -->
    <AssistantPage
      v-if="!bidUid"
      eyebrow="Bid Writer"
      title="Bud"
      subtitle="Læst fra Bid Office-mapperne på SharePoint. Igangværende bud først; afleverede og arkiverede ligger nedenunder."
    >
      <template #actions>
        <button type="button" class="btn" @click="emit('exit')">← Hub</button>
        <button type="button" class="btn btn--primary" @click="newOpen = !newOpen">
          {{ newOpen ? "Luk" : "Nyt bud" }}
        </button>
      </template>
      <p v-if="error" class="bw__error">{{ error }}</p>

      <form v-if="newOpen" class="bw__newform" @submit.prevent="createNew">
        <p class="bw__muted bw__small">
          Til et udbud der endnu ikke har en mappe. Brug det UID-nummer I giver mappen — når
          mappen dukker op på SharePoint, kobles den på af sig selv.
        </p>
        <div class="bw__grid">
          <label>UID <input v-model="form.uid" required placeholder="2026-124" pattern="(#?\s*UID-)?[0-9]{4}-[0-9]{1,5}" /></label>
          <label>Kunde <input v-model="form.customer" placeholder="Statens IT" /></label>
          <label class="wide">Titel <input v-model="form.title" placeholder="Servere og storage" /></label>
          <label>Rammeaftale <input v-model="form.framework" placeholder="SKI 50.03" /></label>
          <label>Tidsfrist <input v-model="form.deadline" type="date" /></label>
        </div>
        <div class="bw__actions">
          <button type="submit" class="btn btn--primary" :disabled="creating">{{ creating ? "Opretter…" : "Opret bud" }}</button>
        </div>
      </form>

      <div class="bw__toolbar">
        <input v-model="query" type="search" class="bw__search" placeholder="Søg på UID, kunde, titel eller rammeaftale…" aria-label="Søg" />
        <span class="bw__muted bw__small">{{ filtered.length }} bud</span>
      </div>

      <p v-if="loading && !bids.length" class="bw__muted">Henter…</p>
      <template v-else>
        <section v-if="soon.length" class="bw__group">
          <h2>Tidsfrist inden for en uge <span class="bw__count">{{ soon.length }}</span></h2>
          <ul class="bw__list">
            <li v-for="b in soon" :key="b.uid"><BidRow :b="b" @open="openBid" /></li>
          </ul>
        </section>
        <section class="bw__group">
          <h2>Afventer beslutning <span class="bw__count">{{ undecided.length }}</span></h2>
          <ul v-if="undecided.length" class="bw__list">
            <li v-for="b in undecided" :key="b.uid"><BidRow :b="b" @open="openBid" /></li>
          </ul>
          <p v-else class="bw__muted bw__small">Alle aktive bud har en beslutning.</p>
        </section>
        <section class="bw__group">
          <h2>I gang <span class="bw__count">{{ active.length }}</span></h2>
          <ul v-if="active.length" class="bw__list">
            <li v-for="b in active" :key="b.uid"><BidRow :b="b" @open="openBid" /></li>
          </ul>
          <p v-else class="bw__muted bw__small">Ingen bud besluttet til at byde på{{ query ? " matcher søgningen" : "" }}.</p>
        </section>
        <section v-if="delivered.length" class="bw__group">
          <h2>
            <button type="button" class="bw__disclose" :aria-expanded="deliveredOpen" @click="deliveredOpen = !deliveredOpen">
              {{ deliveredOpen ? "▾" : "▸" }} Afleveret <span class="bw__count">{{ delivered.length }}</span>
            </button>
          </h2>
          <ul v-if="deliveredOpen" class="bw__list">
            <li v-for="b in delivered" :key="b.uid"><BidRow :b="b" @open="openBid" /></li>
          </ul>
        </section>
        <section v-if="declined.length" class="bw__group">
          <h2>
            <button type="button" class="bw__disclose" :aria-expanded="declinedOpen" @click="declinedOpen = !declinedOpen">
              {{ declinedOpen ? "▾" : "▸" }} Fravalgt (no-bid) <span class="bw__count">{{ declined.length }}</span>
            </button>
          </h2>
          <ul v-if="declinedOpen" class="bw__list">
            <li v-for="b in declined" :key="b.uid"><BidRow :b="b" @open="openBid" /></li>
          </ul>
        </section>
        <section class="bw__group">
          <h2>
            <button type="button" class="bw__disclose" :aria-expanded="showArchived" @click="showArchived = !showArchived">
              {{ showArchived ? "▾" : "▸" }} Arkiv <span class="bw__count">{{ showArchived ? archived.length : archivedCount }}</span>
            </button>
          </h2>
          <ul v-if="showArchived && archived.length" class="bw__list">
            <li v-for="b in archived" :key="b.uid"><BidRow :b="b" @open="openBid" /></li>
          </ul>
        </section>
        <p class="bw__muted bw__small bw__foot">
          <template v-if="rebuiltAt">Læst fra Bid Office {{ relTime(rebuiltAt) }}.</template>
          <template v-else>Endnu ikke læst fra Bid Office — listen fyldes ved næste opdatering.</template>
        </p>
      </template>
    </AssistantPage>

    <!-- ── workspace ─────────────────────────────────────────────────── -->
    <AssistantPage
      v-else
      :title="heading"
      :tabs="detail && bid ? workspaceTabs : []"
      :tab="tab"
      :stages="detail && bid ? detail.stages : []"
      :current-stage="currentStage"
      :next-step="detail && bid && detail.next_step.key !== 'done' ? detail.next_step : null"
      @update:tab="onTab"
      @stage="goStage"
      @next="runNext"
    >
      <template #eyebrow>
        <button type="button" class="bw__crumb" @click="gotoList">Bud</button> › <span class="mono">#UID-{{ bidUid }}</span>
      </template>
      <template v-if="bid" #meta>
        <span v-if="bid.framework" class="apill">{{ bid.framework }}</span>
        <span :class="['bw__deadline', deadlineTone(bid)]">
          {{ deadlineLabel(bid) }}<template v-if="bid.deadline"> · {{ fmtDate(bid.deadline) }}</template>
        </span>
        <span class="apill apill--stage">{{ bid.stage_label }}</span>
        <span v-if="bid.decision" :class="['apill', bid.decision === 'nobid' ? 'apill--muted' : 'apill--ok']">{{ bid.decision_label }}</span>
        <span v-else class="apill apill--warn">Beslutning mangler</span>
        <span v-if="bid.archived" class="apill apill--muted">Arkiveret</span>
        <span v-else-if="bid.origin === 'app'" class="apill apill--muted">Ingen mappe endnu</span>
        <a v-if="bid.web_url" :href="bid.web_url" target="_blank" rel="noopener" class="bw__link">Åbn mappen på SharePoint ↗</a>
      </template>
      <template #actions>
        <button type="button" class="btn" @click="emit('exit')">← Hub</button>
        <button type="button" class="btn" @click="gotoList">Alle bud</button>
        <button
          type="button"
          class="btn"
          :disabled="zipping || !detail || detail.counts.generated + rows.length === 0"
          title="Genererede filer og Kravmatrix.xlsx i A–F-strukturen"
          @click="downloadZip"
        >
          {{ zipping ? "Pakker…" : "⤓ Zip" }}
        </button>
      </template>
      <template #banner>
        <p v-if="error" class="bw__error">{{ error }}</p>
        <p v-if="notice" class="bw__notice" role="status">{{ notice }}</p>
        <p v-if="bid && bid.conflicts.length" class="bw__notice" role="alert">
          UID-nummeret #UID-{{ bid.uid }} er også brugt af en anden mappe på SharePoint:
          <span v-for="(c, i) in bid.conflicts" :key="c" class="mono">{{ i ? " · " : "" }}{{ c }}</span>.
          Kun denne mappes filer vises — giv den anden et nyt nummer.
        </p>
        <p v-if="loading && !detail" class="bw__muted">Henter buddet…</p>
      </template>
      <template v-if="detail" #stagesum>
        <span v-if="detail.counts.must_unowned" class="bw__warn">● {{ detail.counts.must_unowned }} Must uden ejer</span>
        <span v-else-if="detail.counts.total" class="bw__ok">● alle Must har en ejer</span>
      </template>

      <template v-if="detail && bid">

        <!-- Beslutning: bid / no-bid, on evidence -->
        <section v-if="tab === 'beslutning'" class="bw__panel">
          <div class="bw__decision">
            <div>
              <div class="bw__eyebrow">Beslutning</div>
              <p class="bw__decision-state">
                <strong>{{ bid.decision_label }}</strong>
                <span v-if="bid.decided_by_name" class="bw__muted bw__small"> · {{ bid.decided_by_name }}, {{ relTime(bid.decided_at) }}</span>
              </p>
              <p v-if="bid.decision_note" class="bw__small">{{ bid.decision_note }}</p>
            </div>
            <div class="bw__decision-actions">
              <input v-model="decisionNote" class="bw__decision-note" type="text" placeholder="Begrundelse (valgfri)" aria-label="Begrundelse" />
              <button type="button" class="btn btn--primary" :disabled="deciding || bid.decision === 'bid'" @click="decide('bid')">Bid</button>
              <button type="button" class="btn" :disabled="deciding || bid.decision === 'nobid'" @click="decide('nobid')">No-bid</button>
              <button type="button" class="btn" :disabled="deciding || bid.decision === 'afventer'" @click="decide('afventer')">Afventer</button>
              <button v-if="bid.decision" type="button" class="btn btn--small" :disabled="deciding" @click="decide('')">Genåbn</button>
            </div>
          </div>

          <h3 class="bw__h3">Beslutningsgrundlag</h3>
          <template v-if="bid.recommendation && bid.recommendation.criteria?.length">
            <p class="bw__rec">
              <strong>Assistentens anbefaling: {{ REC_LABEL[bid.recommendation.recommendation] ?? bid.recommendation.recommendation }}</strong>
              <span v-if="bid.recommendation.summary"> — {{ bid.recommendation.summary }}</span>
              <span class="bw__muted bw__small"> · {{ bid.recommendation.by }}, {{ relTime(bid.recommendation.at) }}</span>
            </p>
            <div class="bw__tablewrap">
              <table class="bw__table bw__score">
                <thead><tr><th></th><th>Kriterium</th><th>Evidens</th><th>Kilde</th></tr></thead>
                <tbody>
                  <tr v-for="(c, i) in bid.recommendation.criteria" :key="i" :class="`v-${c.verdict}`">
                    <td class="bw__verdict" :title="c.verdict">{{ VERDICT_MARK[c.verdict] ?? "?" }}</td>
                    <td><strong>{{ c.name }}</strong></td>
                    <td class="bw__text">{{ c.evidence || "—" }}</td>
                    <td class="bw__muted bw__small">{{ c.source }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div class="bw__scorelists">
              <div v-if="bid.recommendation.disqualifiers?.length">
                <h4>Diskvalificerende krav</h4>
                <ul><li v-for="(d, i) in bid.recommendation.disqualifiers" :key="i">{{ d }}</li></ul>
              </div>
              <div v-if="bid.recommendation.missing?.length">
                <h4>Skal bekræftes af en person</h4>
                <ul><li v-for="(m, i) in bid.recommendation.missing" :key="i">{{ m }}</li></ul>
              </div>
            </div>
            <button type="button" class="btn btn--small" @click="askHelper('Lav et nyt beslutningsgrundlag for bid/no-bid.')">↻ Kør igen</button>
          </template>
          <div v-else class="bw__empty">
            <p>
              Intet beslutningsgrundlag endnu. Hjælperen læser udbudsbetingelser og krav, finder referencer, folk og
              vilkår, og skriver et scorecard her. Beslutningen er jeres.
            </p>
            <button type="button" class="btn btn--primary" :disabled="!hasMaterial" @click="askHelper('Lav et beslutningsgrundlag for bid/no-bid.')">
              Lav beslutningsgrundlag
            </button>
            <p v-if="!hasMaterial" class="bw__muted bw__small">Kræver materiale i mappen eller en upload.</p>
          </div>
        </section>

        <!-- Krav: the spine -->
        <section v-else-if="tab === 'krav'" class="bw__panel">
          <div v-if="!rows.length" class="bw__empty">
            <template v-if="detail.counts.documents">
              <p>
                Ingen kravmatrix endnu. Udbudsmaterialet ligger i mappen på SharePoint
                ({{ detail.counts.documents }} {{ detail.counts.documents === 1 ? "fil" : "filer" }}) — bed hjælperen læse det og
                lave kravmatricen.
              </p>
            </template>
            <template v-else-if="detail.counts.uploads">
              <p>Ingen kravmatrix endnu. Bed hjælperen analysere de uploadede filer.</p>
            </template>
            <template v-else>
              <p>
                Ingen materiale endnu. Læg udbudsmaterialet i A] i mappen på SharePoint (det dukker op her efter
                næste indeksering), eller upload det under Dokumenter.
              </p>
            </template>
            <button
              type="button"
              class="btn btn--primary"
              :disabled="!hasMaterial"
              @click="askHelper('Læs udbudsmaterialet i A] Udbudsmateriale og lav kravmatrix.')"
            >
              Analysér materialet
            </button>
          </div>
          <template v-else>
            <div class="bw__toolbar">
              <div class="bw__filters" role="group" aria-label="Filter">
                <button type="button" :class="['bw__chip', { on: filter === 'alle' }]" @click="filter = 'alle'">Alle {{ counts.alle }}</button>
                <button type="button" :class="['bw__chip', { on: filter === 'must' }]" @click="filter = 'must'">Must {{ counts.must }}</button>
                <button type="button" :class="['bw__chip', { on: filter === 'udenejer' }]" @click="filter = 'udenejer'">Uden ejer {{ counts.udenejer }}</button>
                <button type="button" :class="['bw__chip', { on: filter === 'udensvar' }]" @click="filter = 'udensvar'">Uden svar {{ counts.udensvar }}</button>
              </div>
              <div class="bw__actions">
                <button type="button" class="btn btn--small" @click="askHelper('Læs udbudsmaterialet igen og opdatér kravmatricen (rettelsesblad).')">↻ Opdatér fra materialet</button>
                <button type="button" class="btn btn--small" @click="exportXlsx">⤓ Kravmatrix.xlsx</button>
              </div>
            </div>
            <div class="bw__tablewrap">
            <table class="bw__table">
              <thead>
                <tr>
                  <th>Krav-ID</th><th>Krav</th><th>Type</th><th>Must</th><th>Kilde</th><th>Hvor besvares</th><th>Ejer</th><th>Status</th><th>Ændret</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="r in shown"
                  :key="r.id"
                  :class="{ sel: r.id === selectedId, must: r.must_should === 'Must' }"
                  @click="selectedId = r.id === selectedId ? null : r.id"
                >
                  <td class="mono">{{ r.krav_id }}</td>
                  <td class="bw__text">{{ r.text }}</td>
                  <td>{{ r.req_type }}</td>
                  <td>{{ r.must_should }}</td>
                  <td class="bw__muted bw__small">{{ r.source }}</td>
                  <td @click.stop>
                    <input class="bw__cell" :value="r.answered_in" placeholder="—" @change="setField(r, 'answered_in', ($event.target as HTMLInputElement).value)" />
                  </td>
                  <td @click.stop>
                    <input class="bw__cell" :value="r.owner" placeholder="—" @change="setField(r, 'owner', ($event.target as HTMLInputElement).value)" />
                  </td>
                  <td @click.stop>
                    <select class="bw__cell" :value="r.status" @change="setField(r, 'status', ($event.target as HTMLSelectElement).value)">
                      <option v-for="s in statuses" :key="s" :value="s">{{ STATUS_LABEL[s] ?? s }}</option>
                    </select>
                  </td>
                  <td class="bw__muted bw__small bw__who" :title="r.updated_at">
                    <template v-if="r.updated_by_name">{{ r.updated_by_name }}<br />{{ relTime(r.updated_at) }}</template>
                  </td>
                </tr>
              </tbody>
            </table>
            </div>
            <aside v-if="selected" class="bw__detail">
              <strong class="mono">{{ selected.krav_id }}</strong>
              <p>{{ selected.text }}</p>
              <p class="bw__muted bw__small">
                Kilde: {{ selected.source || "—" }}
                <template v-if="selected.updated_by_name"> · Ændret af {{ selected.updated_by_name }} {{ relTime(selected.updated_at) }}</template>
              </p>
              <textarea class="bw__note" :value="selected.note" placeholder="Note til teamet…" rows="2" @change="setField(selected, 'note', ($event.target as HTMLTextAreaElement).value)" />
              <div class="bw__detailactions">
                <button type="button" class="btn btn--small" @click="askHelper(`Skriv udkast til besvarelsen af krav ${selected.krav_id}.`)">Skriv udkast til dette krav</button>
                <button type="button" class="btn btn--small" @click="askHelper(`Har vi svaret på noget lignende krav ${selected.krav_id} i tidligere bud?`)">Find tidligere svar</button>
              </div>
            </aside>
          </template>
        </section>

        <!-- Aflevering / Strategi / QA: the artefact if it exists, else the ask -->
        <section v-else-if="tab === 'aflevering' || tab === 'strategi' || tab === 'qa'" class="bw__panel">
          <ul v-if="docsFor(tab).length" class="bw__docs">
            <li v-for="d in docsFor(tab)" :key="d.id">
              <span>{{ d.title }}</span><span class="apill">{{ d.format }}</span>
              <span v-if="!d.mine" class="bw__muted bw__small">af en kollega</span>
              <button type="button" class="btn btn--small" @click="downloadDoc(d)">⤓</button>
            </li>
          </ul>
          <div v-else class="bw__empty">
            <p v-if="tab === 'aflevering'">Ingen afleveringsoversigt endnu — hvad skal afleveres, i hvilket format, med hvilke sidegrænser.</p>
            <p v-else-if="tab === 'strategi'">Ingen angrebsplan endnu — vinderteser og evidens pr. kriterium.</p>
            <p v-else>Ingen QA endnu — kør den når besvarelsen er samlet.</p>
            <button
              type="button"
              class="btn btn--primary"
              :disabled="!rows.length && tab !== 'aflevering'"
              @click="askHelper(tab === 'aflevering' ? 'Lav en afleveringsoversigt.' : tab === 'strategi' ? 'Lav en angrebsplan.' : 'Kør kvalitetssikring af besvarelsen.')"
            >
              {{ tab === "aflevering" ? "Lav afleveringsoversigt" : tab === "strategi" ? "Lav angrebsplan" : "Kør QA" }}
            </button>
          </div>
        </section>

        <!-- Dokumenter: the folder on SharePoint, what was generated here, and own uploads -->
        <section v-else class="bw__panel bw__docsgrid">
          <div>
            <h3>Mappen på SharePoint <span class="bw__count">{{ detail.counts.documents || "" }}</span></h3>
            <p v-if="!detail.counts.documents" class="bw__muted bw__small">
              <template v-if="bid.origin === 'app'">Ingen mappe endnu. Når #UID-{{ bid.uid }} oprettes på SharePoint, vises filerne her efter næste indeksering.</template>
              <template v-else>Ingen filer indekseret fra mappen endnu.</template>
            </p>
            <ul class="bw__folders">
              <li v-for="f in detail.folders" :key="f.name" :class="{ empty: !f.files.length }">
                <div class="bw__folder">{{ f.name }} <span class="bw__count">{{ f.files.length || "" }}</span></div>
                <ul v-if="f.files.length" class="bw__files">
                  <li v-for="e in f.files" :key="e.source">
                    <a v-if="e.url" :href="e.url" target="_blank" rel="noopener" class="bw__filename">{{ e.name }}</a>
                    <span v-else class="bw__filename">{{ e.name }}</span>
                  </li>
                </ul>
              </li>
            </ul>
          </div>
          <div>
            <h3>Genereret her <span class="bw__count">{{ detail.counts.generated || "" }}</span></h3>
            <p v-if="!detail.generated.length" class="bw__muted bw__small">Ingen genererede filer endnu. Det hjælperen bygger lander her, i A–F-strukturen, og kan hentes som zip.</p>
            <ul v-else class="bw__docs">
              <li v-for="d in detail.generated" :key="d.id">
                <span class="bw__muted bw__small mono">{{ d.folder.slice(0, 2) || "—" }}</span>
                <span>{{ d.title }}</span><span class="apill">{{ d.format }}</span>
                <span v-if="!d.mine" class="bw__muted bw__small">af en kollega</span>
                <button type="button" class="btn btn--small" @click="downloadDoc(d)">⤓</button>
              </li>
              <li v-if="rows.length" class="bw__virtual">
                <span class="bw__muted bw__small mono">C]</span>
                <span>Kravmatrix</span><span class="apill">xlsx · fra rækker</span>
                <button type="button" class="btn btn--small" @click="exportXlsx">⤓</button>
              </li>
            </ul>

            <h3 class="bw__h3gap">Dine uploads <span class="bw__count">{{ detail.counts.uploads || "" }}</span></h3>
            <p class="bw__muted bw__small">Til materiale der ikke ligger i mappen endnu. Kun du og hjælperen ser dem.</p>
            <ul v-if="detail.uploads.length" class="bw__files">
              <li v-for="u in detail.uploads" :key="u.id">
                <span class="bw__filename">{{ u.name }}</span>
                <span v-if="u.index_status && u.index_status !== 'ready'" class="apill">{{ u.index_status }}</span>
              </li>
            </ul>
            <label class="btn btn--small bw__upload">
              {{ uploading ? "Uploader…" : "Upload filer (A])" }}
              <input type="file" multiple :disabled="uploading || conversationId === null" hidden @change="onFiles" />
            </label>
          </div>
        </section>
      </template>

      <HelperPanel
        :helper="helper"
        title="Bid Writer"
        hint="kender buddet og dets filer"
        empty="Spørg om buddet, bed om en analyse, et udkast eller en QA. Kravmatricen og filerne den bygger deles med hele teamet."
        :dev-user="devUser"
        :disabled="conversationId === null"
      />
    </AssistantPage>
  </div>
</template>

<script lang="ts">
import { defineComponent, h, type PropType } from "vue";
import type { Bid as BidT } from "../api/bids";

/** One bid in the list. Local component so the four groups share it. */
const BidRow = defineComponent({
  name: "BidRow",
  props: { b: { type: Object as PropType<BidT>, required: true } },
  emits: ["open"],
  setup(props, { emit }) {
    return () => {
      const b = props.b;
      const n = (() => {
        if (!b.deadline) return null;
        const d = new Date(`${b.deadline}T00:00:00`);
        const t = new Date();
        t.setHours(0, 0, 0, 0);
        return Math.round((d.getTime() - t.getTime()) / 86_400_000);
      })();
      const dl = (() => {
        if (n === null) return b.deadline_text || "Ingen tidsfrist";
        if (n === 0) return "I dag";
        if (n === 1) return "I morgen";
        if (n > 0) return `Om ${n} dage`;
        return `${b.deadline.split("-").reverse().join(".")}`;
      })();
      const tone = n === null || n < 0 ? "" : n <= 3 ? "hot" : n <= 7 ? "warn" : "";
      const r = b.requirements;
      const krav = !r.total
        ? h("span", { class: "bw__muted" }, "Ingen kravmatrix")
        : r.must_unowned
          ? h("span", { class: "bw__warn" }, `${r.total} krav · ${r.must_unowned} Must uden ejer`)
          : h("span", {}, `${r.total} krav · ${r.answered}/${r.total} besvaret`);
      return h(
        "button",
        { type: "button", class: "bw__row", onClick: () => emit("open", b.uid) },
        [
          h("span", { class: "bw__row-uid mono" }, `#UID-${b.uid}`),
          h("span", { class: "bw__row-main" }, [
            h("span", { class: "bw__row-customer" }, b.customer || b.title || b.folder_name),
            b.customer && b.title ? h("span", { class: "bw__muted" }, ` — ${b.title}`) : null,
            b.framework ? h("span", { class: "apill" }, b.framework) : null,
            b.origin === "app" ? h("span", { class: "apill apill--muted" }, "ingen mappe endnu") : null,
            b.phase === "aktiv" && !b.decision ? h("span", { class: "apill apill--warn" }, "beslutning mangler") : null,
            b.decision === "afventer" ? h("span", { class: "apill apill--warn" }, "afventer beslutning") : null,
            b.decision === "nobid" ? h("span", { class: "apill apill--muted" }, "no-bid") : null,
            b.conflicts?.length ? h("span", { class: "apill apill--warn", title: b.conflicts.join(" · ") }, "UID brugt to gange") : null,
          ]),
          h("span", { class: "bw__row-krav bw__small" }, [krav]),
          h("span", { class: "apill apill--stage" }, b.stage_label),
          h("span", { class: ["bw__row-deadline", tone] }, dl),
        ],
      );
    };
  },
});
export default { components: { BidRow } };
</script>

<style scoped>
.bw { width: 100%; }
.bw__crumb { background: none; border: none; padding: 0; font: inherit; color: inherit; cursor: pointer; text-decoration: underline; }
.bw__warn { color: #b45309; }
.bw__ok { color: #15803d; }
.bw__error { color: #b00020; }
.bw__notice { background: #fff7e6; border: 1px solid #f0d9a8; border-radius: 6px; padding: 0.4rem 0.6rem; font-size: 0.85rem; }
.bw__muted { color: var(--muted, #777); }
.bw__small { font-size: 0.8rem; }
.bw__link { font-size: 0.85rem; }
.mono { font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-size: 0.85em; }
.bw__decision { display: flex; justify-content: space-between; align-items: flex-start; gap: 1rem; flex-wrap: wrap; padding: 0.75rem; border: 1px solid var(--color-border, #e2e2e2); border-radius: 8px; margin-bottom: 1rem; }
.bw__decision-state { margin: 0.1rem 0 0.2rem; font-size: 1.05rem; }
.bw__decision-actions { display: flex; gap: 0.4rem; align-items: center; flex-wrap: wrap; }
.bw__decision-note { min-width: 14rem; padding: 0.4rem 0.5rem; border: 1px solid var(--color-border, #ccc); border-radius: 6px; font: inherit; }
.bw__h3 { font-size: 0.95rem; margin: 0 0 0.4rem; }
.bw__rec { margin: 0 0 0.5rem; }
.bw__score tr.v-stop td { background: #fdecec; }
.bw__score tr.v-risk td { background: #fff7e6; }
.bw__score tr.v-ok td:first-child { color: #15803d; }
.bw__verdict { font-weight: 700; text-align: center; width: 2rem; }
.bw__score tr { cursor: default; }
.bw__scorelists { display: grid; grid-template-columns: repeat(auto-fit, minmax(16rem, 1fr)); gap: 1rem; margin: 0.75rem 0; }
.bw__scorelists h4 { margin: 0 0 0.25rem; font-size: 0.85rem; }
.bw__scorelists ul { margin: 0; padding-left: 1.1rem; font-size: 0.85rem; }
.bw__deadline.warn { color: #b45309; font-weight: 600; }
.bw__deadline.hot { color: #b00020; font-weight: 600; }

/* list */
.bw__newform { border: 1px solid var(--border, #e2e2e2); border-radius: 8px; padding: 0.75rem; margin: 0.5rem 0 1rem; }
.bw__grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 0.5rem 0.75rem; margin: 0.5rem 0; }
.bw__grid label { display: flex; flex-direction: column; font-size: 0.8rem; color: var(--muted, #555); gap: 0.2rem; }
.bw__grid label.wide { grid-column: span 2; }
.bw__grid input { padding: 0.4rem 0.5rem; border: 1px solid var(--border, #ccc); border-radius: 6px; font: inherit; }
.bw__toolbar { display: flex; justify-content: space-between; align-items: center; gap: 0.75rem; margin: 0.5rem 0; flex-wrap: wrap; }
.bw__search { flex: 1; min-width: 16rem; padding: 0.45rem 0.6rem; border: 1px solid var(--border, #ccc); border-radius: 6px; font: inherit; }
.bw__group { margin-top: 1rem; width: 100%; }
.bw__group h2 { font-size: 0.95rem; margin: 0 0 0.4rem; }
.bw__disclose { background: none; border: none; padding: 0; font: inherit; font-weight: 600; cursor: pointer; color: inherit; }
.bw__list { list-style: none; margin: 0; padding: 0; }
.bw__row { width: 100%; display: grid; grid-template-columns: 8.5rem 1fr 14rem 9rem 8rem; gap: 0.75rem; align-items: center; padding: 0.55rem 0.75rem; border: 1px solid var(--border, #e2e2e2); border-radius: 8px; background: transparent; margin-bottom: 0.35rem; cursor: pointer; text-align: left; font: inherit; color: inherit; }
.bw__row:hover { background: var(--hover, #f6f6f7); }
.bw__row-main { display: flex; align-items: center; gap: 0.4rem; flex-wrap: wrap; min-width: 0; }
.bw__row-customer { font-weight: 600; }
.bw__row-deadline { text-align: right; white-space: nowrap; }
.bw__row-deadline.warn { color: #b45309; font-weight: 600; }
.bw__row-deadline.hot { color: #b00020; font-weight: 600; }
.bw__foot { margin-top: 1.25rem; }
@media (max-width: 900px) {
  .bw__row { grid-template-columns: 1fr; gap: 0.2rem; }
  .bw__row-deadline { text-align: left; }
  .bw__grid { grid-template-columns: 1fr 1fr; }
}

/* workspace */
.bw__panel { min-height: 24rem; width: 100%; min-width: 0; }
.bw__tablewrap { width: 100%; overflow-x: auto; }
.bw__filters { display: flex; gap: 0.3rem; flex-wrap: wrap; }
.bw__chip { border: 1px solid var(--border, #d0d0d0); background: transparent; border-radius: 999px; padding: 0.2rem 0.6rem; font-size: 0.8rem; cursor: pointer; }
.bw__chip.on { background: var(--accent, #1f4e79); color: #fff; border-color: transparent; }
.bw__table { width: 100%; border-collapse: collapse; font-size: 0.85rem; table-layout: auto; }
.bw__table th { text-align: left; font-weight: 600; padding: 0.35rem 0.4rem; border-bottom: 1px solid var(--border, #ddd); white-space: nowrap; }
.bw__table td { padding: 0.3rem 0.4rem; border-bottom: 1px solid var(--border, #eee); vertical-align: top; }
.bw__table tr { cursor: pointer; }
.bw__table tr.sel { background: var(--hover, #f3f6fa); }
.bw__table tr.must td:first-child { border-left: 3px solid #b45309; }
.bw__text { max-width: 30rem; }
.bw__who { white-space: nowrap; line-height: 1.2; }
.bw__cell { width: 100%; min-width: 6rem; padding: 0.2rem 0.3rem; border: 1px solid transparent; border-radius: 4px; background: transparent; font: inherit; }
.bw__cell:hover, .bw__cell:focus { border-color: var(--border, #ccc); background: #fff; }
.bw__detail { margin-top: 0.75rem; padding: 0.75rem; border: 1px solid var(--border, #e2e2e2); border-radius: 8px; }
.bw__detail p { margin: 0.25rem 0; }
.bw__note { width: 100%; margin-top: 0.4rem; padding: 0.4rem; border: 1px solid var(--border, #ddd); border-radius: 6px; font: inherit; }
.bw__detailactions { display: flex; gap: 0.4rem; margin-top: 0.5rem; flex-wrap: wrap; }
.bw__empty { padding: 1.5rem; text-align: center; color: var(--muted, #666); }
.bw__empty p { margin: 0 0 0.75rem; }
.bw__docs { list-style: none; padding: 0; margin: 0; }
.bw__docs li { display: flex; align-items: center; gap: 0.5rem; padding: 0.4rem 0; border-bottom: 1px solid var(--border, #eee); }
.bw__docs li .btn { margin-left: auto; }
.bw__docsgrid { display: grid; grid-template-columns: minmax(0, 1.2fr) minmax(0, 1fr); gap: 1.5rem; width: 100%; }
.bw__docsgrid > div { min-width: 0; }
.bw__docsgrid h3 { font-size: 0.95rem; margin: 0 0 0.4rem; }
.bw__h3gap { margin-top: 1.25rem !important; }
@media (max-width: 900px) { .bw__docsgrid { grid-template-columns: 1fr; } }
.bw__upload { cursor: pointer; margin-top: 0.5rem; display: inline-block; }
.bw__folders { list-style: none; padding: 0; margin: 0; }
.bw__folders > li { margin-bottom: 0.4rem; }
.bw__folders > li.empty .bw__folder { color: var(--muted, #999); font-weight: 400; }
.bw__folder { font-weight: 500; }
.bw__files { list-style: none; margin: 0.15rem 0 0 1rem; padding: 0; }
.bw__files li { display: flex; align-items: center; gap: 0.4rem; padding: 0.15rem 0; }
.bw__filename { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.bw__virtual { opacity: 0.85; }
</style>
